import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Typography,
  TextField,
  Button,
  Stack,
  Tooltip,
  Divider,
  Chip,
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";

import { v4 as uuidv4 } from "uuid";
import AdminNavbar from "../components/AdminNavbar";

import { sendAdminChat, uploadAdminImage, getApiBase, fetchAdminProjects } from "../services/api";

// --- IMPORTANT (tiny helper): add this to your axios api module ---
// export function setAdminAuthToken(token) {
//   if (!token) return;
//   axiosInstance.defaults.headers["Authorization"] = `Bearer ${token}`;
//   axiosInstance.defaults.headers["ngrok-skip-browser-warning"] = "1"; // if tunneling
// }
// export async function sendMessageAdmin({ text, session_id, lang }) {
//   return sendMessage({ text, session_id, gender: "none", lang, admin: true });
// }

// -- iOS/Android keyboard-safe viewport  
function useMobileVH() {
  useEffect(() => {
    const setVh = () => {
      const vh = window.visualViewport ? window.visualViewport.height * 0.01 : window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setVh();
    window.addEventListener("resize", setVh);
    window.visualViewport?.addEventListener("resize", setVh);
    return () => {
      window.removeEventListener("resize", setVh);
      window.visualViewport?.removeEventListener("resize", setVh);
    };
  }, []);
}

// Browser feature checks
const hasSTT = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
const hasTTS = !!window.speechSynthesis;

// Δημιουργία SpeechRecognition instance (lazy)
function createRecognizer(lang = "el-GR") {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  const rec = new SR();
  rec.lang = lang;
  rec.continuous = true;
  rec.interimResults = true;
  rec.maxAlternatives = 1;
  return rec;
}

// Εκφώνηση κειμένου (TTS)
function speak(text, lang = "el-GR") {
  if (!hasTTS) return;
  window.speechSynthesis.cancel(); // κόψε ό,τι παίζει
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  // Προαιρετικά: βρες ελληνική φωνή αν υπάρχει
  const voices = window.speechSynthesis.getVoices();
  const gr = voices.find(v => v.lang?.toLowerCase().startsWith("el"));
  if (gr) u.voice = gr;
  window.speechSynthesis.speak(u);
}

// function stopListening() {
//   try { recRef.current?.stop(); } catch {}
//   setListening(false);
// }

const sessionKey = "admin_chat_session_id";
const getSessionId = () => {
  let sid = localStorage.getItem(sessionKey);
  if (!sid) {
    sid = `admin_${uuidv4()}`;
    localStorage.setItem(sessionKey, sid);
  }
  return sid;
};

function useClipboard() {
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      console.warn("Clipboard failed", e);
      return false;
    }
  };
  return { copyText };
}

function downloadBlob(content, filename, type = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function parseMarkdownBlocks(md) {
  // Very small parser: splits into {type: 'text'|'code', lang?, content}
  const blocks = [];
  const codeRegex = /```(\w+)?\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  while ((match = codeRegex.exec(md)) !== null) {
    if (match.index > lastIndex) {
      blocks.push({ type: "text", content: md.slice(lastIndex, match.index) });
    }
    blocks.push({ type: "code", lang: match[1] || "", content: match[2] });
    lastIndex = codeRegex.lastIndex;
  }
  if (lastIndex < md.length) {
    blocks.push({ type: "text", content: md.slice(lastIndex) });
  }
  return blocks;
}

function TextBlock({ content }) {
  // minimal markdown: **bold**, *italic*, \n -> <br/>
  const html = useMemo(() => {
    let h = content
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, "<code style='padding:2px 4px;border-radius:6px;background:#f1f5f9'>$1</code>")
      .replace(/\n/g, "<br/>");
    return h.trim();
  }, [content]);
  return (
    <Typography
      component="div"
      sx={{ fontSize: 15.5, lineHeight: 1.65 }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function CodeBlock({ lang, code }) {
  const { copyText } = useClipboard();
  const preRef = useRef(null);

  const fileExt = lang || "txt";
  const filename = `snippet.${fileExt}`;

  return (
      <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0", bgcolor: "#0b1020" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.2, py: 0.5, borderBottom: "1px solid #1f2937", bgcolor: "#0f172a" }}>
          <Typography variant="caption" color="#cbd5e1">{lang || "code"}</Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => copyText(code)}>
                <ContentCopyIcon fontSize="inherit" sx={{ color: "#e2e8f0" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download file">
              <IconButton size="small" onClick={() => downloadBlob(code, filename)}>
                <DownloadIcon fontSize="inherit" sx={{ color: "#e2e8f0" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        <pre ref={preRef} style={{ margin: 0, padding: "14px 16px", color: "#e2e8f0", overflowX: "auto", fontSize: 13.5 }}>
          <code>{code}</code>
        </pre>
      </Box>
  );
}

function MessageBubble({ role, text }) {
  const blocks = useMemo(() => parseMarkdownBlocks(text || ""), [text]);
  const isUser = role === "user";

  return (
    <Card
      elevation={isUser ? 3 : 1}
      sx={{
        maxWidth: "100%",
        bgcolor: isUser ? "primary.main" : "background.paper",
        color: isUser ? "primary.contrastText" : "text.primary",
        borderRadius: 3,
        borderTopLeftRadius: isUser ? 3 : 1,
        borderTopRightRadius: isUser ? 1 : 3,
      }}
    >
      <CardContent sx={{ p: 1.5 }}>
        {blocks.map((b, i) =>
          b.type === "code" ? (
            <Box key={i} sx={{ my: 1 }}>
              <CodeBlock lang={b.lang} code={b.content} />
            </Box>
          ) : (
            <Box key={i} sx={{ my: 0.5 }}>
              <TextBlock content={b.content} />
            </Box>
          )
        )}
      </CardContent>
    </Card>
  );
}

async function requestMicPermission() {
  // 1) Υποστήριξη & secure context
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    const insecure = typeof window.isSecureContext !== "undefined" && !window.isSecureContext;
    const reason = insecure && window.location.hostname !== "localhost"

      ? "Χρειάζεται https ή localhost."
      : "Ο browser δεν υποστηρίζει getUserMedia.";
    const err = new TypeError(reason);
    err.name = "TypeError";
    throw err;
  }

  if (window.isSecureContext === false && window.location.hostname !== "localhost") {
    const err = new Error("Χρειάζεται https ή localhost.");
    err.name = "SecurityError";
    throw err;
  }

  // 2) Trigger permission prompt
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (err) {
    throw err; // θα το πιάσουμε πιο κάτω στο startListening
  } finally {
    // κλείσε αμέσως το mic για να μην «κρατάει» η ροή
    try { stream?.getTracks()?.forEach(t => t.stop()); } catch {}
  }
  return true;
}


export default function AdminChatPage({ token, onLogout }) {
  const [messages, setMessages] = useState(() => [
    { role: "assistant", content: "👋 Καλωσήρθες στο Admin Chat. ..." },
  ]);
  const [input, setInput] = useState("");
  const [lang] = useState("el");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const fileInputRef = useRef(null);


  // 👉 ΒΑΛ' ΤΟ ΠΑΝΩ-ΠΑΝΩ
  const sid = useMemo(() => getSessionId(), []);

  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const [ttsOn, setTtsOn] = useState(true); // on by default

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);


  useMobileVH();


  const stopListening = useCallback(() => {
    try { recRef.current?.stop(); } catch {}
    setListening(false);
  }, []);

  useEffect(() => {
    // setAdminAuthToken(token);
  }, [token]);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);

  useEffect(() => {
    (async () => {
      if (!token) return; // προαιρετικό guard για 401 spam
      try {
        const res = await fetch(`/api/admin/conversations/${sid}/messages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const msgs = await res.json();
        if (Array.isArray(msgs) && msgs.length) {
          setMessages([
            { role: "assistant", content: "📜 Επαναφορά προηγούμενης συνομιλίας." },
            ...msgs.map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.content }))
          ]);
        }
      } catch {}
    })();
  }, [sid, token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      const res = await fetchAdminProjects(token);
      if (Array.isArray(res)) setProjects(res);
    })();
  }, [token]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    try {
      const data = await sendAdminChat({ text, lang, session_id: sid, project_id: selectedProject, }, token);
      if (data?.error === "UNAUTHORIZED") {
        setMessages(m => [...m, { role: "assistant", content: "🔒 Η συνεδρία έληξε. Κάνε ξανά login." }]);
        return;
      }
      if (data?.error) {
        setMessages(m => [...m, { role: "assistant", content: `⚠️ ${data.error}` }]);
        return;
      }
      let reply = data?.reply || "(χωρίς απάντηση)";
      if (data?.download_url) {
        const link = data.download_url.startsWith("http")
          ? data.download_url
          : `${window.location.origin}${data.download_url}`;
        reply += `\n\n**Αρχείο έτοιμο για λήψη:** ${link}`;
      }
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      console.error(err);
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Σφάλμα κατά την αποστολή. Δοκίμασε ξανά." }]);
    } finally {
      setBusy(false); // <-- κρίσιμο
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");

  const exportReplyTxt = () => {
    const content = lastAssistant?.content || "";
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    downloadBlob(content, `assistant-reply-${ts}.txt`, "text/plain;charset=utf-8");
  };

  const exportReplyPdf = async () => {
    // dynamic import to avoid bundling if you don't want deps by default
    try {
      const [{ default: html2canvas }, jsPDFModule] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const { jsPDF } = jsPDFModule;
      const node = document.getElementById("export-last-reply");
      if (!node) return;
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 80;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let y = 40;
      if (imgHeight > pageHeight - 80) {
        // split into multiple pages
        let sY = 0;
        const pageCanvas = document.createElement("canvas");
        const ctx = pageCanvas.getContext("2d");
        const ratio = imgWidth / canvas.width;
        const sliceHeight = (pageHeight - 80) / ratio;
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;
        while (sY < canvas.height) {
          ctx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, sY, canvas.width, sliceHeight, 0, 0, pageCanvas.width, pageCanvas.height);
          const pageImg = pageCanvas.toDataURL("image/png");
          if (y !== 40) pdf.addPage();
          pdf.addImage(pageImg, "PNG", 40, 40, imgWidth, pageHeight - 80);
          sY += sliceHeight;
        }
      } else {
        pdf.addImage(imgData, "PNG", 40, y, imgWidth, imgHeight);
      }
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      pdf.save(`assistant-reply-${ts}.pdf`);
    } catch (e) {
      console.error(e);
      alert("Για PDF χρειάζεται: npm i html2canvas jspdf");
    }
  };

  const exportConversation = () => {
    const payload = JSON.stringify(
      { session_id: sid, created_at: new Date().toISOString(), messages },
      null,
      2
    );
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    downloadBlob(payload, `admin-chat-${ts}.json`, "application/json;charset=utf-8");
  };

  const clearChat = () => {
    setMessages([{ role: "assistant", content: "🧹 Νέα συνομιλία ξεκίνησε." }]);
  };

  // import ImageIcon from "@mui/icons-material/Image";
  // import { uploadAdminImage, getApiBase } from "../services/api";

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await readFileAsBase64(file); // "data:image/png;base64,AAAA..."
      const [header, b64] = (dataUrl || "").split(",");
      const mime =
        (header.match(/^data:(.+);base64$/) || [])[1] || file.type || "image/png";

      const res = await uploadAdminImage({
        fileBase64: b64,
        filename: file.name,
        mime,
        token,
      });

      if (res?.error === "UNAUTHORIZED") {
        setMessages((m) => [
          ...m,
          { role: "assistant", content: "🔒 Η συνεδρία έληξε. Κάνε ξανά login." },
        ]);
        return;
      }
      if (res?.error) {
        setMessages((m) => [
          ...m,
          { role: "assistant", content: `⚠️ ${res.error}` },
        ]);
        return;
      }

      const apiBase = await getApiBase();
      const link = res.url.startsWith("http") ? res.url : `${apiBase}${res.url}`;

      setMessages((m) => [
        ...m,
        { role: "user", content: `📤 Ανέβασα εικόνα: **${res.filename}**` },
        {
          role: "assistant",
          content: `✅ Αποθηκεύτηκε.\n\n**Λήψη:** ${link}\n\n![preview](${link})`,
        },
      ]);
    } catch (err) {
      console.error(err);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "⚠️ Αποτυχία upload εικόνας." },
      ]);
    } finally {
      // καθάρισε input, ξεκλείδωσε UI
      if (e?.target) e.target.value = "";
      setBusy(false);
    }
  };

const startListening = async () => {
  if (!hasSTT || listening) return;

    // Ζήτα άδεια MΙC ΠΡΙΝ ξεκινήσεις το SpeechRecognition
    try {
      await requestMicPermission();
    } catch (ev) {
      const name = ev?.name || "Error";
      let hint = "Έλεγξε τα δικαιώματα μικροφώνου και ξαναφόρτωσε τη σελίδα.";
      if (name === "NotAllowedError") {
        hint = "Το mic είναι μπλοκαρισμένο για το site. Πάτα το λουκετάκι → Microphone → Allow, μετά Reload.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        hint = "Δεν βρέθηκε μικρόφωνο ή είναι λάθος η συσκευή. Διάλεξε σωστή συσκευή στο chrome://settings/content/microphone.";
      } else if (name === "NotReadableError") {
        hint = "Το μικρόφωνο το χρησιμοποιεί άλλη εφαρμογή (Zoom/Teams/OBS). Κλείσ’ τη και δοκίμασε ξανά.";
      } else if (name === "SecurityError") {
        hint = "Άνοιξέ το μέσω https ή σε localhost.";
      } else if (name === "TypeError") {
        hint = ev?.message || "Ο browser δεν υποστηρίζει getUserMedia ή δεν είναι ασφαλές το context.";
      }

      setMessages(m => [...m, { role: "assistant", content: `⚠️ ${name}. ${hint}` }]);
      return;
    }

    const recLang = "el-GR"; // Καρφωμένα ελληνικά
    recRef.current = createRecognizer(recLang);
    let liveText = "";

    recRef.current.onstart = () => console.log("[STT] start", recLang);
    recRef.current.onend = () => setListening(false);
    recRef.current.onerror = (ev) => {
      console.error("[STT] error", ev?.error || ev);
      setMessages(m => [...m, { role: "assistant", content: `⚠️ STT error: ${ev?.error || ev}` }]);
      stopListening();
    };
    recRef.current.onresult = (ev) => {
      let interim = "", final = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) final += t + " ";
        else interim += t + " ";
      }
      if (final) liveText = (liveText + " " + final).trim();
      setInput((liveText + " " + interim).trim());
    };

    recRef.current.start();
    setListening(true);
  };




  useEffect(() => {
    if (!ttsOn || !hasTTS) return;
    const lastMsg = [...messages].reverse().find(m => m.role === "assistant");
    if (!lastMsg?.content) return;
    const plain = lastMsg.content
      .replace(/!\[.*?\]\(.*?\)/g, "")   // εικόνες
      .replace(/\*\*|\*/g, "")           // bold/italic
      .replace(/`{1,3}[\s\S]*?`{1,3}/g, "")
      .replace(/\[(.*?)\]\(.*?\)/g, "$1")
      .replace(/[#>-]/g, "")
      .trim();
    if (plain) speak(plain, (lang || "el").toLowerCase().startsWith("el") ? "el-GR" : "en-US");
  }, [messages, ttsOn, lang]);


  return (
    <>
     <AdminNavbar onLogout={onLogout} />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        <Card elevation={2} sx={{ borderRadius: 4, overflow: "hidden" }}>
          <CardHeader
            sx={{
              "& .MuiCardHeader-content": { minWidth: 0 },
              "& .MuiCardHeader-action": { alignSelf: { xs: "center", md: "flex-start" } },
              py: { xs: 1.25, md: 1.5 },
            }}
            title={
              <Typography variant="h6" noWrap>
                Admin Chat Assistant
              </Typography>
            }
            subheader={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={`Session: ${sid.slice(0, 12)}…`} size="small" />
                <Chip label={`Lang: ${lang}`} size="small" />
              </Stack>
            }
            action={
              <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="flex-end">
                <Tooltip title="New chat">
                  <IconButton size="small" onClick={clearChat}><RestartAltIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Export .txt">
                  <IconButton size="small" onClick={exportReplyTxt}><DownloadIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Export PDF">
                  <IconButton size="small" onClick={exportReplyPdf}><PictureAsPdfIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Export .json">
                  <IconButton size="small" onClick={exportConversation}><AutoFixHighIcon /></IconButton>
                </Tooltip>
              </Stack>
            }
          />

          <Divider />

          <CardContent
            sx={{
              p: { xs: 1.5, md: 2.5 },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, md: 2 },
              minHeight: {
                xs: "calc((var(--vh, 1vh) * 100) - 180px)", // ασφαλές για mobile keyboards
                md: 480,
              },
            }}
          >
            {/* Messages area */}
            <Box
              ref={scrollRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: { xs: 1.25, md: 2 },
                bgcolor: "background.default",
                scrollBehavior: "smooth",
              }}
            >
              <Stack spacing={1.25}>
                {messages.map((m, idx) => (
                  <Box
                    key={idx}
                    id={idx === messages.length - 1 && m.role === "assistant" ? "export-last-reply" : undefined}
                    sx={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}
                  >
                    <Box sx={{ maxWidth: { xs: "92%", md: "80%" } }}>
                      <MessageBubble role={m.role === "assistant" ? "assistant" : "user"} text={m.content} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>

            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="project-select-label">Έργο</InputLabel>
              <Select
                labelId="project-select-label"
                value={selectedProject || ""}
                onChange={(e) => setSelectedProject(e.target.value)}
                label="Έργο"
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


            {/* Input bar – sticky bottom, mobile-first */}
            <Box
              component="form"
              onSubmit={(e) => { e.preventDefault(); send(); }}
              sx={{
                position: { xs: "sticky", md: "static" },
                bottom: 0,
                zIndex: 1,
                borderTop: { xs: "1px solid", md: "none" },
                borderColor: "divider",
                bgcolor: { xs: "background.paper", md: "transparent" },
                px: { xs: 1, md: 0 },
                pb: { xs: "max(8px, env(safe-area-inset-bottom))", md: 0 },
                pt: { xs: 1, md: 0 },
              }}
            >
              {/* Σε κινητό κάνουμε column, σε desktop row */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 1, md: 1 }}
                alignItems={{ xs: "stretch", md: "flex-end" }}
              >
                {/* Πάνω σειρά: input */}
                <TextField
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ρώτησε για κώδικα, SQL, APIs, debugging…"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={6}
                  disabled={busy}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: 2.5,
                      bgcolor: "background.paper",
                      px: 1.25,
                    },
                  }}
                />

                {/* Κάτω σειρά: actions (wrap σε mobile) */}
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent={{ xs: "space-between", md: "flex-start" }}
                  sx={{ mt: { xs: 0.5, md: 0 } }}
                >
                  {/* Upload */}
                  <Tooltip title="Upload εικόνας">
                    <span>
                      <input
                        ref={fileInputRef}
                        id="admin-image-input"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        style={{ display: "none" }}
                        onChange={handleImagePick}
                      />
                      <IconButton
                        size="large"
                        disabled={busy}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ borderRadius: 2, bgcolor: { xs: "#f1f5f9", md: "transparent" } }}
                      >
                        <ImageIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Attach (disabled placeholder) */}
                  <Tooltip title="(προαιρετικά) επισύναψη μελλοντικά">
                    <span>
                      <IconButton size="large" disabled sx={{ borderRadius: 2 }}>
                        <AttachFileIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* STT */}
                  <Tooltip title={listening ? "Διακοπή φωνής" : "Μίλα για να γράψει"}>
                    <span>
                      <IconButton
                        size="large"
                        onClick={listening ? stopListening : startListening}
                        disabled={!hasSTT || busy}
                        sx={{ borderRadius: 2 }}
                      >
                        {listening ? <MicOffIcon /> : <MicIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* TTS */}
                  <Tooltip title={ttsOn ? "Σίγαση εκφώνησης" : "Ενεργοποίηση εκφώνησης"}>
                    <span>
                      <IconButton
                        size="large"
                        onClick={() => {
                          const next = !ttsOn;
                          setTtsOn(next);
                          if (!next && hasTTS) window.speechSynthesis.cancel();
                        }}
                        disabled={!hasTTS}
                        sx={{ borderRadius: 2 }}
                      >
                        {ttsOn ? <VolumeUpIcon /> : <VolumeOffIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Send – πάντα ορατό, full-width σε κινητό αν δεν χωράει */}
                  <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    endIcon={<SendIcon />}
                    disabled={busy || !input.trim()}
                    sx={{
                      ml: "auto",
                      px: { xs: 2, md: 2.25 },
                      py: { xs: 1.2, md: 0.9 },
                      borderRadius: 2,
                      minWidth: { xs: 120, md: "auto" },
                      fontWeight: 700,
                    }}
                  >
                    Αποστολή
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </CardContent>

        </Card>
      </Box>
    </>  
  );
}
